function parseJsonScript(el) {
  if (!el) return null;
  try {
    const data = JSON.parse(el.textContent);
    if (data) {
      data._colorOptionIndex = Array.isArray(data.options)
        ? data.options.findIndex((n) =>
            /^(color|colour|farbe)$/i.test(String(n).trim()),
          )
        : -1;
    }
    return data;
  } catch {
    return null;
  }
}

function formatAddonMoney(cents) {
  const amount = Number(cents || 0);
  if (typeof window.formatMoney === "function" && typeof window.moneyFormat === "string") {
    try {
      return window.formatMoney(amount, window.moneyFormat);
    } catch {}
  }
  try {
    return new Intl.NumberFormat(document.documentElement.lang || "de-DE", {
      style: "currency",
      currency: window.Shopify?.currency?.active || "EUR",
    }).format(amount / 100);
  } catch {
    return `${(amount / 100).toFixed(2)} €`;
  }
}

function parseMoneyToCents(str) {
  if (!str) return NaN;
  const cleaned = String(str).replace(/[^\d.,]/g, "");
  if (!cleaned) return NaN;

  const decPos = Math.max(cleaned.lastIndexOf("."), cleaned.lastIndexOf(","));
  let intPart, fracPart;

  if (decPos === -1) {
    intPart = cleaned;
    fracPart = "";
  } else {
    const digitsAfter = cleaned.length - decPos - 1;
    // 1–2 Ziffern nach dem letzten Trenner => Dezimaltrenner, sonst Tausendertrenner
    if (digitsAfter >= 1 && digitsAfter <= 2) {
      intPart = cleaned.slice(0, decPos).replace(/[.,]/g, "");
      fracPart = cleaned.slice(decPos + 1);
    } else {
      intPart = cleaned.replace(/[.,]/g, "");
      fracPart = "";
    }
  }

  const value = parseFloat(fracPart ? `${intPart}.${fracPart}` : intPart);
  return Number.isFinite(value) ? Math.round(value * 100) : NaN;
}

function emit(el, name, detail = {}) {
  el.dispatchEvent(
    new CustomEvent(name, { bubbles: true, composed: true, detail }),
  );
}

function changeDpoObververForm() {
  // priceHandler bewusst nicht gesetzt — DPO soll seinen eigenen Flow behalten
}

class AddonStepper extends HTMLElement {
  static get observedAttributes() {
    return ["quantity", "max", "disabled"];
  }

  connectedCallback() {
    if (this._rendered) return;

    this._rendered = true;
    this._qty = Number(this.getAttribute("quantity") || 0);
    this._max = Number(this.getAttribute("max") || 0);
    this._render();
    this._bindClicks();
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (!this._rendered) return;
    if (name === "quantity") {
      this._qty = Math.max(0, this._clamp(Number(newVal || 0)));
      this._update();
    }
    if (name === "max") {
      this._max = Math.max(0, Number(newVal || 0));
      if (this._max > 0 && this._qty > this._max) {
        this._qty = this._max;
      }
      this._update();
    }
    if (name === "disabled") {
      this._update();
    }
  }

  get quantity() {
    return this._qty;
  }
  get max() {
    return this._max;
  }
  get disabled() {
    return this.hasAttribute("disabled");
  }

  set quantity(val) {
    const n = this._clamp(Math.max(0, Number(val)));
    if (n === this._qty) return;
    this._qty = n;
    this.setAttribute("quantity", n);
    this._update();
  }

  set max(val) {
    const n = Math.max(0, Number(val));
    this._max = n;
    this.setAttribute("max", n);
    if (n > 0 && this._qty > n) this.quantity = n;
    this._update();
  }

  set disabled(val) {
    this.toggleAttribute("disabled", val);
  }

  reset() {
    this.quantity = 0;
  }

  _clamp(n) {
    if (this._max > 0) return Math.min(n, this._max);
    return n;
  }

  _addLabel() {
    return (
      this.dataset.addLabel ||
      this.closest("addon-products")?.dataset.addLabel ||
      "Hinzufügen"
    );
  }

  _render() {
    this.setAttribute("role", "group");
    this.innerHTML = `
      <button type="button" class="addon-stepper__btn addon-stepper__decrement" aria-label="Menge verringern" hidden>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="1" y1="6" x2="11" y2="6"/>
        </svg>
      </button>
      <span class="addon-stepper__qty" aria-live="polite" aria-atomic="true" hidden>0</span>
      <button type="button" class="addon-stepper__btn addon-stepper__increment" aria-label="Hinzufügen">
        <span class="addon-stepper__add-label">${this._addLabel()}</span>
        <svg class="addon-stepper__plus-icon" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
          <line x1="6" y1="1" x2="6" y2="11"/>
          <line x1="1" y1="6" x2="11" y2="6"/>
        </svg>
      </button>
    `;

    this._btnDecrement = this.querySelector(".addon-stepper__decrement");
    this._btnIncrement = this.querySelector(".addon-stepper__increment");
    this._qtyEl = this.querySelector(".addon-stepper__qty");
    this._addLabelEl = this.querySelector(".addon-stepper__add-label");

    this._update();
  }

  _bindClicks() {
    this._btnIncrement?.addEventListener("click", () => {
      if (this.disabled) return;
      if (this._max > 0 && this._qty >= this._max) return;
      const prev = this._qty;
      this.quantity = this._qty + 1;
      emit(this, "addon:stepper-change", {
        quantity: this._qty,
        previousQuantity: prev,
      });
    });

    this._btnDecrement?.addEventListener("click", () => {
      if (this.disabled) return;
      const prev = this._qty;
      this.quantity = this._qty - 1;
      emit(this, "addon:stepper-change", {
        quantity: this._qty,
        previousQuantity: prev,
      });
    });
  }

  _update() {
    if (!this._rendered) return;

    const isZero = this._qty === 0;
    const isDisabled = this.disabled;
    const atMax = this._max > 0 && this._qty >= this._max;

    this._btnDecrement.hidden = isZero;
    this._qtyEl.hidden = isZero;
    this._addLabelEl.hidden = !isZero;
    this._qtyEl.textContent = String(this._qty);
    this._btnIncrement.setAttribute(
      "aria-label",
      isZero ? this._addLabel() : `Menge erhöhen (aktuell ${this._qty})`,
    );
    this._btnDecrement.setAttribute(
      "aria-label",
      `Menge verringern (aktuell ${this._qty})`,
    );
    this._btnDecrement.disabled = isDisabled;
    this._btnIncrement.disabled = isDisabled || atMax;
    this.dataset.qty = this._qty;

    if (atMax) this.dataset.atMax = "true";
    else delete this.dataset.atMax;
  }
}

class AddonItem extends HTMLElement {
  connectedCallback() {
    this._data = parseJsonScript(this.querySelector("[data-addon-variants]"));
    this._bindSelect();
    this._bindStepper();
    requestAnimationFrame(() => this._updateSelectDisabledState());
  }

  _updateSelectDisabledState() {
    const select = this._select;
    if (!select) return;
    const hasSelectable = Array.from(select.options).some((o) => !o.disabled);
    select.disabled = !hasSelectable;

    if (!hasSelectable && !select._addonDisabledObserver) {
      select._addonDisabledObserver = new MutationObserver(() => {
        const stillAllDisabled = Array.from(select.options).every(
          (o) => o.disabled,
        );
        if (stillAllDisabled && !select.disabled) {
          select.disabled = true;
        }
      });
      select._addonDisabledObserver.observe(select, {
        attributes: true,
        attributeFilter: ["disabled"],
      });
    } else if (hasSelectable && select._addonDisabledObserver) {
      select._addonDisabledObserver.disconnect();
      delete select._addonDisabledObserver;
    }
  }

  get productId() {
    return this.dataset.addonProductId || "";
  }
  get groupKey() {
    return this.dataset.addonGroupKey || "";
  }
  get groupOrder() {
    return Number(this.dataset.addonGroupOrder || 999);
  }

  get variantId() {
    return String(this._select?.value || this._hiddenInput?.value || "");
  }

  get checked() {
    const s = this._stepper;
    if (!s || s.disabled || this._stepperWrap?.hidden) return false;
    return s.quantity > 0;
  }

  get quantity() {
    return this._stepper?.quantity ?? 0;
  }

  get currentVariant() {
    return (
      this._data?.variants?.find((v) => String(v.id) === this.variantId) ?? null
    );
  }

  setVariant(id) {
    if (this._select) this._select.value = String(id);
    else if (this._hiddenInput) this._hiddenInput.value = String(id);
    this._refresh();
  }

  syncToBaseColor(colorIndex, baseColor) {
    if (!this._data) return false;

    const addonColorIndex = this._data._colorOptionIndex ?? -1;
    if (addonColorIndex === -1) return false;
    const normalized = String(baseColor).toLowerCase();
    const selectableIds = this._getSelectableVariantIds();

    const isSelectable = (v) =>
      selectableIds.length === 0 || selectableIds.includes(String(v.id));
    const addonColor = (v) =>
      String(v.options[addonColorIndex] || "").toLowerCase();
    const baseWords = normalized.split(/[\s\-\/]+/).filter(Boolean);
    const wordsMatch = (v) =>
      baseWords.every((w) =>
        addonColor(v)
          .split(/[\s\-\/]+/)
          .includes(w),
      );

    const match =
      this._data.variants.find(
        (v) =>
          Array.isArray(v.options) &&
          addonColor(v) === normalized &&
          isSelectable(v),
      ) ??
      this._data.variants.find(
        (v) =>
          Array.isArray(v.options) &&
          addonColor(v).includes(normalized) &&
          isSelectable(v),
      ) ??
      this._data.variants.find(
        (v) => Array.isArray(v.options) && wordsMatch(v) && isSelectable(v),
      );
    if (!match || this.variantId === String(match.id)) return false;
    this.setVariant(match.id);
    return true;
  }

  updateImage() {
    return this._loadImage();
  }

  get _select() {
    return this.querySelector("[data-addon-variant-select]");
  }
  get _hiddenInput() {
    return this.querySelector("[data-addon-variant-input]");
  }
  get _stepper() {
    return this.querySelector("addon-stepper");
  }
  get _stepperWrap() {
    return this.querySelector("[data-addon-stepper-wrap]");
  }
  get _priceEl() {
    return this.querySelector("[data-addon-price]");
  }
  get _imageEl() {
    return this.querySelector("[data-addon-image]");
  }
  get _mediaWrapper() {
    return this.querySelector(".product-addons__item-media");
  }

  _bindSelect() {
    this._select?.addEventListener("change", () => {
      this._refresh();
      emit(this, "addon:variant-changed", { item: this });
    });
  }

  _bindStepper() {
    this.addEventListener("addon:stepper-change", (e) => {
      e.stopPropagation();
      this._updatePrice();
      emit(this, "addon:selection-changed", {
        item: this,
        quantity: e.detail.quantity,
      });

      const prevQty = e.detail.previousQuantity;
      const newQty = e.detail.quantity;
      if (prevQty !== undefined) {
        const root = this.closest("addon-products");
        if (
          root &&
          ((prevQty === 0 && newQty === 1) || (prevQty > 0 && newQty === 0))
        ) {
          root._publishItemToggle(this, newQty);
        }
      }
    });
  }

  _refresh() {
    this._updateAvailability();
    this._updatePrice();
    this._loadImage();
  }

  _getSelectableVariantIds() {
    if (!this._select) return [];
    return Array.from(this._select.options)
      .filter((o) => !o.disabled)
      .map((o) => String(o.value));
  }

  _updateAvailability() {
    const { _stepperWrap, _stepper, _select, _data } = this;
    if (!_stepperWrap || !_stepper || !_data?.variants) return;

    if (_select) {
      const selectableOptions = Array.from(_select.options).filter(
        (o) => !o.disabled,
      );
      _select.disabled = selectableOptions.length === 0;

      const selectedOption = _select.options[_select.selectedIndex];
      if (selectedOption?.disabled && selectableOptions.length > 0) {
        _select.value = selectableOptions[0].value;
        this._loadImage();
      }
    }

    const variant = this.currentVariant;
    const available = Boolean(variant?.available);
    const soldoutEl = _stepperWrap.querySelector("[data-addon-soldout]");

    _stepper.hidden = !available;
    _stepper.disabled = !available;
    if (soldoutEl) soldoutEl.hidden = available;

    if (!available) {
      _stepper.reset();
    } else {
      const stock = Number(variant?.inventory_quantity ?? -1);
      _stepper.max = stock >= 0 ? stock : 0;
    }
  }

  _updatePrice() {
    const { _priceEl, _data } = this;
    if (!_priceEl || !_data?.variants) return;
    const variant = this.currentVariant;
    if (!variant) return;
    const qty = this.quantity;

    const currentEl =
      _priceEl.querySelector("[data-addon-price-current]") || _priceEl;
    const compareEl = _priceEl.querySelector("[data-addon-price-compare]");

    const price = Number(variant.price) || 0;
    const compareAt = Number(variant.compare_at_price) || 0;
    const onSale = compareAt > price;

    currentEl.textContent =
      qty > 1 ? `${qty} × +${formatAddonMoney(price)}` : `+${formatAddonMoney(price)}`;

    if (compareEl) {
      if (onSale) {
        compareEl.textContent =
          qty > 1 ? `${qty} × ${formatAddonMoney(compareAt)}` : formatAddonMoney(compareAt);
        compareEl.hidden = false;
      } else {
        compareEl.textContent = "";
        compareEl.hidden = true;
      }
    }

    _priceEl.classList.toggle("is-on-sale", onSale);
  }

  _loadImage() {
    const { _imageEl, _mediaWrapper, _data } = this;
    if (!_imageEl || !_mediaWrapper || !_data?.variants)
      return Promise.resolve();

    const variant = this.currentVariant;
    const newSrc = variant?.image ?? null;

    if (!newSrc || _imageEl.getAttribute("src") === newSrc) {
      this._setLoading(false);
      return Promise.resolve();
    }

    this._setLoading(true);

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        _imageEl.src = newSrc;
        _imageEl.removeAttribute("srcset");
        _imageEl.alt = variant?.title || _imageEl.alt || "";
        if (_imageEl.dataset.src) _imageEl.dataset.src = newSrc;
        this._setLoading(false);
        resolve();
      };
      img.onerror = () => {
        this._setLoading(false);
        resolve();
      };
      img.src = newSrc;
    });
  }

  _setLoading(on) {
    const w = this._mediaWrapper;
    if (!w) return;
    w.classList.toggle("is-loading", on);
    if (on) w.dataset.addonPreloading = "true";
    else delete w.dataset.addonPreloading;
  }
}

class AddonGroup extends HTMLElement {
  connectedCallback() {
    this.addEventListener("addon:selection-changed", () => this._update());
    this.addEventListener("addon:variant-changed", () => this._update());
    this._bindAccordion();
  }

  _bindAccordion() {
    const details = this.querySelector("details[data-addon-group]");
    const summary = details?.querySelector("summary");
    const wrapper = details?.querySelector(".addon-category__content-wrapper");
    if (!details || !summary || !wrapper) return;

    summary.addEventListener("click", (e) => {
      e.preventDefault();

      if (details.open) {
        const content = wrapper.querySelector(".addon-category__content");
        const needsScroll = content && content.scrollHeight > 500;
        
        if (content && !needsScroll) content.style.overflowY = "hidden";
        wrapper.classList.add("is-closing");
        wrapper.addEventListener("transitionend", () => {
          wrapper.classList.remove("is-closing");
          details.removeAttribute("open");
          if (content) content.style.overflowY = "";
        }, { once: true });
      } else {
        details.setAttribute("open", "");
        const content = wrapper.querySelector(".addon-category__content");
        const needsScroll = content && content.scrollHeight > 500;

        if (content && !needsScroll) content.style.overflowY = "hidden";
        requestAnimationFrame(() => {
          wrapper.classList.add("is-opening");
          wrapper.addEventListener("transitionend", () => {
            wrapper.classList.remove("is-opening");
            if (content) content.style.overflowY = "";
          }, { once: true });
        });
      }
    });
  }

  get items() {
    return Array.from(this.querySelectorAll("addon-item"));
  }
  get selectedItems() {
    return this.items.filter((i) => i.checked);
  }

  setLoading(on) {
    this.querySelector(".addon-category__loader")?.classList.toggle(
      "is-loading",
      on,
    );
  }

  async updateImages() {
    await Promise.all(this.items.map((i) => i.updateImage()));
    this._updateThumbnail();
  }

  _update() {
    this._updateCount();
    this._updateThumbnail();
    this.classList.toggle("has-selection", this.selectedItems.length > 0);
  }

  _updateCount() {
    const countEl = this.querySelector("[data-addon-group-count]");
    if (!countEl) return;

    const selected = this.selectedItems.length;
    const total = this.items.length;
    const root = this.closest("addon-products");

    if (selected > 0) {
      const selectedLabel = root?.dataset.selectedLabel || "ausgewählt";
      countEl.innerHTML =
        `<span class="addon-count__prefix">✔</span>` +
        `<span class="addon-count__label">${selectedLabel}: </span>` +
        `<span class="addon-count__numbers">${selected} / ${total}</span>`;
      this.classList.add("has-selection");
    } else {
      const countLabel = root?.dataset.countLabel || "";
      countEl.innerHTML =
        `<span class="addon-count__numbers">0 / ${total}</span>` +
        `<span class="addon-count__suffix"> ${countLabel}</span>`;
      this.classList.remove("has-selection");
    }
  }

  _updateThumbnail() {
    const thumbnail = this.querySelector("[data-addon-group-thumbnail]");
    if (!thumbnail) return;
    const firstItem = this.querySelector("addon-item");
    if (!firstItem) return;
    const newSrc =
      firstItem.currentVariant?.image ??
      firstItem.querySelector("[data-addon-image]")?.getAttribute("src");
    if (newSrc && thumbnail.getAttribute("src") !== newSrc) {
      thumbnail.src = newSrc;
      thumbnail.removeAttribute("srcset");
    }
  }
}

class AddonSummary extends HTMLElement {
  connectedCallback() {
    const root = this.closest("addon-products");
    if (!root) return;
    root.addEventListener("addon:selection-changed", () => this.update(root));
    root.addEventListener("addon:variant-changed", () => this.update(root));
    root.addEventListener("addon:color-sync-done", () => this.update(root));
  }

  update(root) {
    const totalEl = this.querySelector("[data-addon-summary-total]");
    const addonsEl = this.querySelector("[data-addon-summary-addons]");
    if (!totalEl || !addonsEl || !root) return;

    const basePrice = root.basePrice;
    const addonsTotal = root.addonsTotal;
    const grandTotal = basePrice + addonsTotal;
    const totalLabel =
      this.dataset.totalLabel || root.dataset.totalLabel || "Gesamt";

    totalEl.textContent = `${totalLabel}: ${formatAddonMoney(grandTotal)}`;

    if (addonsTotal > 0) {
      const template =
        this.dataset.addonsLabel ||
        root.dataset.addonsLabel ||
        "inkl. {{ price }} Add-ons";
      addonsEl.textContent = template.replace(
        "{{ price }}",
        formatAddonMoney(addonsTotal),
      );
      addonsEl.hidden = false;
    } else {
      addonsEl.textContent = "";
      addonsEl.hidden = true;
    }
  }
}

class AddonStrip extends HTMLElement {
  connectedCallback() {
    const root = this.closest("addon-products");
    if (!root) return;

    root.addEventListener("addon:selection-changed", () => this.update(root));
    root.addEventListener("addon:color-sync-done", () => this.update(root));

    this.addEventListener("click", (e) => {
      if (root._isLocked) return;
      const btn = e.target.closest("[data-addon-selection-remove]");
      if (!btn) return;

      const productId = String(btn.getAttribute("data-addon-product-id") || "");
      const variantId = String(btn.getAttribute("data-addon-variant-id") || "");

      const item = root.allItems.find(
        (i) => i.productId === productId && i.variantId === variantId,
      );
      if (!item) return;

      const stepper = item.querySelector("addon-stepper");
      if (stepper) {
        const prev = stepper.quantity;
        const next = Math.max(0, prev - 1);
        stepper.quantity = next;
        emit(stepper, "addon:stepper-change", {
          quantity: next,
          previousQuantity: prev,
        });

        if (next > 0) {
          btn.classList.add("addon-selection-strip__addon--pulse");
          btn.addEventListener(
            "animationend",
            () => {
              btn.classList.remove("addon-selection-strip__addon--pulse");
            },
            { once: true },
          );
        }
      }
    });
  }

  update(root) {
    const wrapper = this.querySelector("[data-addon-selection-strip-wrapper]");
    const addonsContainer = this.querySelector("[data-addon-selection-addons]");
    if (!wrapper || !addonsContainer || !root) return;

    wrapper.hidden = false;

    const showLabels = this.hasAttribute("data-show-group-labels");
    const baseLabel = this.dataset.baseLabel || "";

    const baseImage = root.baseImage;
    let baseGroup = addonsContainer.querySelector(
      '.addon-selection-strip__group[data-group-key="__base"]',
    );
    if (baseImage) {
      if (!baseGroup) {
        baseGroup = document.createElement("div");
        baseGroup.className = "addon-selection-strip__group";
        baseGroup.dataset.groupKey = "__base";

        if (showLabels && baseLabel) {
          const labelEl = document.createElement("span");
          labelEl.className = "addon-selection-strip__group-label";
          labelEl.textContent = baseLabel;
          baseGroup.appendChild(labelEl);
        }

        const imagesDiv = document.createElement("div");
        imagesDiv.className = "addon-selection-strip__group-images";
        const img = document.createElement("img");
        img.className =
          "addon-selection-strip__image addon-selection-strip__image--base";
        img.alt =
          document
            .querySelector('h1.product__title, h1[itemprop="name"]')
            ?.textContent?.trim() || "";
        img.src = baseImage;
        imagesDiv.appendChild(img);
        baseGroup.appendChild(imagesDiv);
        addonsContainer.prepend(baseGroup);
      } else {
        const img = baseGroup.querySelector(
          ".addon-selection-strip__image--base",
        );
        if (img && img.src !== baseImage) img.src = baseImage;
      }
    }

    const groups = [];
    const groupMap = new Map();

    root.selectedItems.forEach((item) => {
      const key = item.groupKey;
      if (!groupMap.has(key)) {
        const groupEl = [...root.querySelectorAll("addon-group")].find((g) =>
          [...g.querySelectorAll("addon-item")].some((i) => i.groupKey === key),
        );
        groupMap.set(key, {
          key,
          label: groupEl?.dataset.addonGroupLabel || "",
          items: [],
        });
        groups.push(groupMap.get(key));
      }
      groupMap.get(key).items.push(item);
    });

    const newKeys = new Set(groups.map((g) => g.key));
    newKeys.add("__base");

    // Platzhalter anzeigen wenn keine Addons gewählt, sonst entfernen
    const placeholder = addonsContainer.querySelector(
      ".addon-selection-strip__placeholder",
    );
    if (groups.length === 0) {
      if (!placeholder) {
        const ph = document.createElement("a");
        ph.className = "addon-selection-strip__placeholder";
        ph.href = "#addon-categories";
        ph.setAttribute(
          "aria-label",
          this.dataset.placeholderLabel || "Add-ons hinzufügen",
        );
        ph.innerHTML =
          '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="10" y1="3" x2="10" y2="17"/><line x1="3" y1="10" x2="17" y2="10"/></svg>';
        ph.style.animation = "strip-item-in 0.25s ease forwards";
        ph.addEventListener("click", () => {
          const firstDetails = document.querySelector(
            "addon-group details[data-addon-group]",
          );
          if (firstDetails && !firstDetails.open) {
            firstDetails.querySelector("summary")?.click();
          }
        });
        addonsContainer.appendChild(ph);
      }
    } else if (placeholder) {
      placeholder.remove();
    }

    // Gruppen die verschwinden sollen entfernen
    addonsContainer
      .querySelectorAll(".addon-selection-strip__group[data-group-key]")
      .forEach((el) => {
        const key = el.dataset.groupKey;
        if (
          !newKeys.has(key) &&
          !el.classList.contains("addon-selection-strip__group--removing")
        ) {
          if (groups.length === 0) {
            el.remove();
          } else {
            el.classList.add("addon-selection-strip__group--removing");
            el.addEventListener("animationend", () => el.remove(), {
              once: true,
            });
          }
        }
      });

    groups.forEach(({ key, label, items }) => {
      let groupDiv = addonsContainer.querySelector(
        `.addon-selection-strip__group[data-group-key="${CSS.escape(key)}"]`,
      );
      const isNew = !groupDiv;

      if (isNew) {
        groupDiv = document.createElement("div");
        groupDiv.className = "addon-selection-strip__group";
        groupDiv.dataset.groupKey = key;

        if (showLabels && label) {
          const labelEl = document.createElement("span");
          labelEl.className = "addon-selection-strip__group-label";
          labelEl.textContent = label;
          groupDiv.appendChild(labelEl);
        }

        const imagesDiv = document.createElement("div");
        imagesDiv.className = "addon-selection-strip__group-images";
        groupDiv.appendChild(imagesDiv);
        addonsContainer.appendChild(groupDiv);
      }

      const imagesDiv = groupDiv.querySelector(
        ".addon-selection-strip__group-images",
      );

      const existingBtns = new Map();
      imagesDiv.querySelectorAll("[data-addon-variant-id]").forEach((btn) => {
        existingBtns.set(btn.getAttribute("data-addon-variant-id"), btn);
      });

      const newVariantIds = new Set(items.map((i) => i.variantId));

      existingBtns.forEach((btn, vid) => {
        if (
          !newVariantIds.has(vid) &&
          !btn.classList.contains("addon-selection-strip__group--removing")
        ) {
          btn.style.animation = "strip-item-out 0.2s ease forwards";
          btn.addEventListener("animationend", () => btn.remove(), {
            once: true,
          });
        }
      });

      items.forEach((item) => {
        const variant = item.currentVariant;
        if (!variant) return;

        const title =
          item
            .querySelector(".product-addons__item-title")
            ?.childNodes?.[0]?.textContent?.trim() ||
          variant.title ||
          "";
        const qty = item.quantity;
        const vid = item.variantId;

        if (existingBtns.has(vid)) {
          const btn = existingBtns.get(vid);
          let badge = btn.querySelector(".addon-selection-strip__badge");
          if (qty > 1) {
            if (!badge) {
              badge = document.createElement("span");
              badge.className = "addon-selection-strip__badge";
              badge.setAttribute("aria-label", `${qty}×`);
              btn.appendChild(badge);
            }
            badge.textContent = String(qty);
          } else {
            badge?.remove();
          }
        } else {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.className = "addon-selection-strip__addon";
          btn.setAttribute("data-addon-selection-remove", "");
          btn.setAttribute("data-addon-product-id", item.productId);
          btn.setAttribute("data-addon-variant-id", vid);
          btn.setAttribute("aria-label", `${title} entfernen`);
          btn.title = title;
          btn.style.animation = "strip-item-in 0.25s ease forwards";

          const img = document.createElement("img");
          img.className = "addon-selection-strip__image";
          img.alt = title;
          if (variant.image) img.src = variant.image;
          btn.appendChild(img);

          if (qty > 1) {
            const badge = document.createElement("span");
            badge.className = "addon-selection-strip__badge";
            badge.textContent = String(qty);
            badge.setAttribute("aria-label", `${qty}×`);
            btn.appendChild(badge);
          }

          imagesDiv.appendChild(btn);
        }
      });
    });
  }
}

class AddonProducts extends HTMLElement {
  constructor() {
    super();
    this._syncTimeout = null;
    this._preloadTimer = null;
    this._isReady = false;
    this._isLocked = false;
    this._productForm = null;
    this._dpoActive = false;
  }

  connectedCallback() {
    if (this._productForm) return;

    this._productForm = this._findProductForm();
    if (!this._productForm) {
      console.warn("[addon-products] Produkt-Formular nicht gefunden.");
      return;
    }

    this.addEventListener("addon:selection-changed", () => {
      this._updateExpressCheckout();
    });

    this._bindBaseColorPreloadHint();
    this._bindBaseColorSync();
    this._bindBasePriceSync();
    this._initDpo();
  }

_initDpo() {
  waitForDpo(() => {
    changeDpoObververForm();
    this._dpoActive = true;
    this._bindDpoCartHook();
  });
  this._bindSubmit();
}

  _bindBasePriceSync() {
    const priceContainer = document.querySelector(".price--medium");
    if (!priceContainer) return;

    // DPO/Cart-Rerender tauscht .price--medium komplett aus -> ein Observer direkt
    // darauf stirbt nach dem ersten Austausch. Daher stabilen Vorfahren beobachten.
    const observeRoot =
      priceContainer.closest('[id^="shopify-section-"]') ||
      priceContainer.parentElement ||
      priceContainer;

    let pending = false;
    new MutationObserver((mutations) => {
      // Eigene Schreibvorgänge in <addon-products> ignorieren -> keine Endlosschleife
      if (!mutations.some((m) => !this.contains(m.target))) return;
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        emit(this, "addon:variant-changed", {});
      });
    }).observe(observeRoot, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  _bindDpoCartHook() {
    const self = this;
    const origOpen = XMLHttpRequest.prototype.open;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      if (url?.includes("cart/update")) {
        const selectedAddons = self.selectedItems;
        if (selectedAddons.length) {
          const addonFor = window.ShopifyAnalytics?.meta?.product?.handle ?? "";
          const items = [];
          selectedAddons.forEach((item) => {
            for (let i = 0; i < item.quantity; i++) {
              items.push({
                id: Number(item.variantId),
                quantity: 1,
                properties: { _addon_for: addonFor },
              });
            }
          });

          fetch(`${window.Shopify.routes.root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ items }),
            keepalive: true,
          }).catch((err) =>
            console.error("[addon-products] DPO Addon-Cart-Fehler:", err),
          );
        }
      }
      return origOpen.call(this, method, url, ...rest);
    };
  }

  get allItems() {
    return Array.from(this.querySelectorAll("addon-item"));
  }

  get selectedItems() {
    return this.allItems
      .filter((i) => i.checked)
      .sort((a, b) => {
        if (a.groupOrder !== b.groupOrder) return a.groupOrder - b.groupOrder;
        if (a.groupKey !== b.groupKey)
          return a.groupKey.localeCompare(b.groupKey, undefined, {
            sensitivity: "base",
          });
        const pa = Number(a.currentVariant?.price || 0);
        const pb = Number(b.currentVariant?.price || 0);
        if (pa !== pb) return pa - pb;
        return (a.currentVariant?.title || "").localeCompare(
          b.currentVariant?.title || "",
          undefined,
          { sensitivity: "base" },
        );
      });
  }

  get allGroups() {
    return Array.from(this.querySelectorAll("addon-group"));
  }

  get addonsTotal() {
    return this.selectedItems.reduce(
      (s, i) => s + Number(i.currentVariant?.price || 0) * i.quantity,
      0,
    );
  }

  get basePrice() {
    const priceBlock =
      document.querySelector(".price--medium") ||
      this._productForm
        ?.closest('[id^="shopify-section-"]')
        ?.querySelector(".price");

    if (priceBlock) {
      // Theme versteckt den nicht-aktiven Preis per display:none.
      // Nur den sichtbaren, befüllten Preis-Knoten lesen — verhindert, dass ein
      // befülltes, aber verstecktes .price-item--sale nach DPO-/Cart-Rerender gewinnt.
      const candidates = priceBlock.querySelectorAll(
        ".price-item--sale, .price-item--regular",
      );
      for (const el of candidates) {
        if (el.offsetParent === null) continue; // versteckt -> überspringen
        const txt = el.textContent.trim();
        if (!txt) continue;
        const parsed = parseMoneyToCents(txt);
        if (!isNaN(parsed)) return parsed;
      }
    }
    return Number(this._currentBaseVariant()?.price || 0);
  }

  get baseImage() {
    return this._currentBaseVariant()?.image ?? null;
  }

  _baseData() {
    return parseJsonScript(this.querySelector("[data-base-product-variants]"));
  }

  _currentBaseVariant() {
    const data = this._baseData();
    if (!data?.variants) return null;
    const id = Number(this._productForm?.querySelector('[name="id"]')?.value);
    return id ? (data.variants.find((v) => Number(v.id) === id) ?? null) : null;
  }

  _baseColorValue() {
    const data = this._baseData();
    const variant = this._currentBaseVariant();
    if (!data || !variant || !Array.isArray(variant.options)) return null;
    const idx = data._colorOptionIndex ?? -1;
    return idx !== -1 ? (variant.options[idx] ?? null) : null;
  }

  _findProductForm() {
    const root =
      this.closest('[id^="shopify-section-"]') ||
      this.closest(".shopify-section") ||
      document;
    return (
      root.querySelector('form[data-type="add-to-cart-form"]') ||
      root.querySelector("product-form form") ||
      root.querySelector('form[action*="/cart/add"]')
    );
  }

  _getOptionGroups() {
    return Array.from(
      this._productForm.querySelectorAll(
        "fieldset, .product-form__input, .product__input, .selector-wrapper",
      ),
    ).filter((g) =>
      g.querySelector('input[type="radio"], input[type="checkbox"], select'),
    );
  }

  _getControlFromTarget(target) {
    if (!(target instanceof Element)) return null;
    if (target.matches("input, select")) return target;
    const lf = target.closest("label[for]");
    if (lf) {
      const id = lf.getAttribute("for");
      return id ? this._productForm.querySelector("#" + CSS.escape(id)) : null;
    }
    return target.closest("label")?.querySelector("input, select") ?? null;
  }

  _isBaseColorControl(control) {
    if (!control) return false;
    const colorIndex = this._baseData()?._colorOptionIndex ?? -1;
    if (colorIndex === -1) return false;
    return this._optionIndexForControl(control) === colorIndex;
  }

  _optionIndexForControl(control) {
    const optionNames = this._baseData()?.options ?? [];
    const name = String(control.name || "");

    const bracketMatch = name.match(/^options\[(.+)\]$/i);
    if (bracketMatch && optionNames.length) {
      const needle = bracketMatch[1].trim().toLowerCase();
      const idx = optionNames.findIndex(
        (o) => String(o).trim().toLowerCase() === needle,
      );
      if (idx !== -1) return idx;
    }

    const numericMatch = name.match(/option(?:s)?[-_]?(\d+)/i);
    if (numericMatch) return Number(numericMatch[1]) - 1;

    const group = control.closest(
      "fieldset, .product-form__input, .product__input, .selector-wrapper",
    );
    return group ? this._getOptionGroups().indexOf(group) : -1;
  }

  _lock(on) {
    this._isLocked = on;
    this.dataset.addonUiLocked = on ? "true" : "false";

    this.allGroups.forEach((g) => g.setLoading(on));

    this.allItems.forEach((item) => {
      const stepper = item.querySelector("addon-stepper");
      const select = item.querySelector("[data-addon-variant-select]");

      if (stepper) {
        if (on) {
          stepper.dataset.wasDisabled = stepper.disabled ? "true" : "false";
          stepper.disabled = true;
        } else {
          stepper.disabled = stepper.dataset.wasDisabled === "true";
          delete stepper.dataset.wasDisabled;
        }
      }

      if (select) {
        if (on) {
          select.dataset.wasDisabled = select.disabled ? "true" : "false";
          select.disabled = true;
        } else {
          select.disabled = select.dataset.wasDisabled === "true";
          delete select.dataset.wasDisabled;
        }
      }
    });

    emit(this, "addon:ui-lock", { locked: on });
  }

  _bindBaseColorPreloadHint() {
    if (this._productForm.dataset.addonPreloadBound === "true") return;
    this._productForm.dataset.addonPreloadBound = "true";

    this._productForm.addEventListener(
      "click",
      (e) => {
        if (this._isLocked) return;

        const control = this._getControlFromTarget(e.target);
        if (!control || !this._isBaseColorControl(control) || control.disabled)
          return;

        const nextColor = String(control.value || "").trim();
        const currentColor = this._baseColorValue();
        if (!nextColor) return;
        if (
          String(currentColor || "").toLowerCase() === nextColor.toLowerCase()
        )
          return;
        if (
          control.matches('input[type="radio"], input[type="checkbox"]') &&
          control.checked
        )
          return;

        this._lock(true);
        this._preloadForColor(nextColor);
      },
      true,
    );
  }

  _preloadForColor(color) {
    clearTimeout(this._preloadTimer);
    const colorIndex = this._baseData()?._colorOptionIndex ?? -1;
    if (colorIndex === -1) return;

    this.allItems.forEach((item) => {
      const select = item.querySelector("[data-addon-variant-select]");
      const media = item.querySelector(".product-addons__item-media");
      const idata = parseJsonScript(
        item.querySelector("[data-addon-variants]"),
      );
      if (
        !select ||
        !media ||
        !idata ||
        !idata.options ||
        (idata._colorOptionIndex ?? -1) === -1
      )
        return;

      const normalizedColor = color.toLowerCase();
      const selectableOptIds = Array.from(select.options)
        .filter((o) => !o.disabled)
        .map((o) => String(o.value));
      const isOpt = (v) => selectableOptIds.includes(String(v.id));
      const aColor = (v) => String(v.options[colorIndex] || "").toLowerCase();
      const match =
        idata.variants.find(
          (v) =>
            Array.isArray(v.options) &&
            aColor(v) === normalizedColor &&
            isOpt(v),
        ) ??
        idata.variants.find(
          (v) =>
            Array.isArray(v.options) &&
            aColor(v).includes(normalizedColor) &&
            isOpt(v),
        );

      if (!match || String(select.value) === String(match.id)) return;
      media.classList.add("is-loading");
      media.dataset.addonPreloading = "true";
    });

    this._preloadTimer = setTimeout(() => this._clearPreloads(), 1800);
  }

  _bindBaseColorSync() {
    if (this._productForm.dataset.addonColorSyncBound === "true") return;
    this._productForm.dataset.addonColorSyncBound = "true";

    const debounced = () => {
      if (!this._isReady) return;
      clearTimeout(this._syncTimeout);
      this._syncTimeout = setTimeout(() => {
        this._syncColors().finally(() => {
          this._lock(false);
          this._clearPreloads();
        });
      }, 50);
    };

    this._productForm.addEventListener("change", debounced);

    const idInput = this._productForm.querySelector('[name="id"]');
    if (idInput && typeof MutationObserver !== "undefined") {
      new MutationObserver(debounced).observe(idInput, {
        attributes: true,
        attributeFilter: ["value"],
      });
      idInput.addEventListener("change", debounced);
      idInput.addEventListener("input", debounced);
    }

    requestAnimationFrame(() => this._initialSync());
  }

  _initialSync() {
    this._lock(true);
    this._waitForBaseVariant()
      .then(() => {
        this._isReady = true;
        return this._syncColors();
      })
      .finally(() => {
        this._lock(false);
        this._clearPreloads();
      });
  }

  _waitForBaseVariant(timeoutMs = 800) {
    return new Promise((resolve) => {
      const deadline = Date.now() + timeoutMs;
      const check = () => {
        if (this._currentBaseVariant() || Date.now() >= deadline)
          return resolve();
        requestAnimationFrame(check);
      };
      check();
    });
  }

  _clearPreloads() {
    clearTimeout(this._preloadTimer);
    this.querySelectorAll('[data-addon-preloading="true"]').forEach((el) => {
      el.classList.remove("is-loading");
      delete el.dataset.addonPreloading;
    });
  }

  async _syncColors() {
    const baseColor = this._baseColorValue();
    const colorIndex = this._baseData()?._colorOptionIndex ?? -1;

    const affectedGroups = new Set();
    const imagePromises = [];
    const allSyncedItems = [];

    for (const group of this.allGroups) {
      for (const item of group.querySelectorAll("addon-item")) {
        const idata = parseJsonScript(
          item.querySelector("[data-addon-variants]"),
        );

        if (
          !idata ||
          !idata.options ||
          (idata._colorOptionIndex ?? -1) === -1
        ) {
          item._refresh();
          allSyncedItems.push({ item, group, colorSynced: false });
          continue;
        }

        if (baseColor && colorIndex !== -1) {
          const changed = item.syncToBaseColor(colorIndex, baseColor);
          if (changed) affectedGroups.add(group);
        }

        allSyncedItems.push({ item, group, colorSynced: true });
        imagePromises.push(item.updateImage().then(() => item._refresh()));
      }
    }

    affectedGroups.forEach((g) => g.setLoading(true));
    await Promise.all(imagePromises);

    allSyncedItems
      .filter(({ colorSynced }) => !colorSynced)
      .forEach(({ item }) => item._refresh());

    affectedGroups.forEach((g) => g.setLoading(false));
    this.allGroups.forEach((g) => g._update?.());

    emit(this, "addon:color-sync-done");
  }

  _updateExpressCheckout() {
    const btn =
      this.closest('[id^="shopify-section-"]')?.querySelector(
        '[data-shopify="payment-button"]',
      ) || document.querySelector('[data-shopify="payment-button"]');
    if (!btn) return;
    btn.hidden = this.selectedItems.length > 0;
  }

  async _validateStock() {
    const selected = this.selectedItems;
    if (!selected.length) return { valid: true, deselectedTitles: [] };

    const availabilityMap = new Map();

    await Promise.all(
      selected.map(async (item) => {
        const variantId = item.variantId;
        if (!variantId || availabilityMap.has(variantId)) return;
        try {
          const res = await fetch(
            `${window.Shopify.routes.root}variants/${variantId}.js`,
            { headers: { Accept: "application/json" } },
          );
          if (!res.ok) return;
          const data = await res.json();
          availabilityMap.set(variantId, {
            available: Boolean(data.available),
            inventory_quantity: Number(data.inventory_quantity ?? -1),
          });
        } catch {}
      }),
    );

    const deselectedTitles = [];

    for (const item of selected) {
      const info = availabilityMap.get(item.variantId);
      if (!info) continue;

      const title =
        item
          .querySelector(".product-addons__item-title")
          ?.childNodes?.[0]?.textContent?.trim() ||
        item.currentVariant?.title ||
        item.variantId;

      if (!info.available) {
        this._deselect(item, title, deselectedTitles);
        continue;
      }

      if (
        info.inventory_quantity >= 0 &&
        item.quantity > info.inventory_quantity
      ) {
        if (info.inventory_quantity === 0) {
          this._deselect(item, title, deselectedTitles);
        } else {
          const stepper = item.querySelector("addon-stepper");
          if (stepper) {
            stepper.max = info.inventory_quantity;
            stepper.quantity = info.inventory_quantity;
            emit(stepper, "addon:stepper-change", {
              quantity: info.inventory_quantity,
            });
          }
        }
      } else {
        const stepper = item.querySelector("addon-stepper");
        if (stepper && info.inventory_quantity >= 0) {
          stepper.max = info.inventory_quantity;
        }
      }
    }

    return { valid: deselectedTitles.length === 0, deselectedTitles };
  }

  _deselect(item, title, titleList) {
    const stepper = item.querySelector("addon-stepper");
    if (stepper) {
      stepper.quantity = 0;
      emit(stepper, "addon:stepper-change", { quantity: 0 });
    }
    titleList.push(title);
  }

  _buildValidationMessage(titles) {
    const template =
      this.dataset.outOfStockMessage ||
      "{{ titles }} ist leider nicht mehr verfügbar und wurde aus deiner Auswahl entfernt. Bitte prüfe deine Auswahl.";
    const formatted =
      titles.length === 1
        ? titles[0]
        : titles.slice(0, -1).join(", ") + " und " + titles[titles.length - 1];
    return template.replace("{{ titles }}", formatted);
  }

  _showValidationError(message) {
    let errEl = this.querySelector("[data-addon-validation-message]");

    if (!errEl) {
      errEl = document.createElement("p");
      errEl.setAttribute("data-addon-validation-message", "");
      errEl.className = "addon-validation-message";
      errEl.setAttribute("role", "alert");
      errEl.setAttribute("aria-live", "assertive");

      const summary = this.querySelector("addon-summary");
      summary ? this.insertBefore(errEl, summary) : this.appendChild(errEl);
    }

    errEl.textContent = message;
    errEl.hidden = false;
    errEl.scrollIntoView({ behavior: "smooth", block: "nearest" });

    clearTimeout(this._validationTimer);
    this._validationTimer = setTimeout(() => {
      errEl.hidden = true;
    }, 6000);
  }

_bindSubmit() {
  if (this._productForm.dataset.addonsBound === "true") return;
  this._productForm.dataset.addonsBound = "true";

  this._productForm.addEventListener(
    "submit",
    async (e) => {
      if (this._dpoActive) return; // DPO übernimmt — nicht eingreifen
      e.preventDefault();
      e.stopImmediatePropagation();

      const submitBtn =
        this._productForm.querySelector('[type="submit"]') ||
        this._productForm.querySelector('button[name="add"]');
      const mainVariantId =
        this._productForm.querySelector('[name="id"]')?.value;
      const mainQuantity = parseInt(
        this._productForm.querySelector('[name="quantity"]')?.value || "1",
        10,
      );

      this._setSubmitLoading(submitBtn, true);

      const { valid, deselectedTitles } = await this._validateStock();

      if (!valid) {
        this._setSubmitLoading(submitBtn, false);
        this._showValidationError(
          this._buildValidationMessage(deselectedTitles),
        );
        return;
      }

      const items = [];
      if (mainVariantId)
        items.push({ id: Number(mainVariantId), quantity: mainQuantity });

      this.selectedItems.forEach((item) => {
        const addonFor = window.ShopifyAnalytics?.meta?.product?.handle ?? "";
        for (let i = 0; i < item.quantity; i++) {
          items.push({
            id: Number(item.variantId),
            quantity: 1,
            properties: { _addon_for: addonFor },
          });
        }
      });

      emit(this, "addon:cart-submit", { items });

      try {
        const res = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items,
            sections: [
              "mini-cart",
              "cart-icon-bubble",
              "mobile-cart-icon-bubble",
            ],
            sections_url: window.location.pathname,
          }),
        });

        const data = await res.json();
        if (!res.ok)
          throw new Error(
            data.description || "Fehler beim Hinzufügen zum Warenkorb.",
          );
        this._renderMiniCart(data, items[0]?.id);
        this._publishAnalytics(items);
      } catch (err) {
        console.error("[addon-products] Warenkorb-Fehler:", err);
        alert(err.message || "Fehler beim Hinzufügen zum Warenkorb.");
      } finally {
        this._setSubmitLoading(submitBtn, false);
      }
    },
    true,
  );
}

  _publishAnalytics(items) {
    if (
      typeof Shopify === "undefined" ||
      typeof Shopify.analytics?.publish !== "function"
    )
      return;

    const baseItem = items[0];
    const addonItems = items.slice(1);
    if (!addonItems.length) return;

    const addonMap = new Map();
    addonItems.forEach((item) => {
      const key = String(item.id);
      if (addonMap.has(key)) {
        addonMap.get(key).qty += 1;
      } else {
        const addonEl = this.allItems.find((i) => String(i.variantId) === key);
        addonMap.set(key, {
          variant_id: item.id,
          product_id: Number(addonEl?.productId || 0),
          qty: 1,
          price: Number(addonEl?.currentVariant?.price || 0) / 100,
        });
      }
    });

    const addons = Array.from(addonMap.values());
    const addonValue = addons.reduce((s, a) => s + a.price * a.qty, 0);

    Shopify.analytics.publish("addon:bundle_added", {
      base_variant_id: Number(baseItem?.id || 0),
      base_product_handle: window.ShopifyAnalytics?.meta?.product?.handle ?? "",
      addon_count: addons.length,
      addon_qty_total: addons.reduce((s, a) => s + a.qty, 0),
      addon_value: Math.round(addonValue * 100) / 100,
      currency: window.Shopify?.currency?.active || "EUR",
      addons,
    });
  }

  _publishItemToggle(item, quantity) {
    if (
      typeof Shopify === "undefined" ||
      typeof Shopify.analytics?.publish !== "function"
    )
      return;
    if (!item) return;

    const variant = item.currentVariant;
    if (!variant) return;

    const title =
      item
        .querySelector(".product-addons__item-title")
        ?.childNodes?.[0]?.textContent?.trim() ||
      variant.title ||
      "";

    Shopify.analytics.publish("addon:item_toggled", {
      action: quantity > 0 ? "added" : "removed",
      variant_id: Number(item.variantId),
      product_id: Number(item.productId),
      product_handle: window.ShopifyAnalytics?.meta?.product?.handle ?? "",
      addon_title: title,
      addon_price: Number(variant.price || 0) / 100,
      currency: window.Shopify?.currency?.active || "EUR",
      group_key: item.groupKey,
    });
  }

  _setSubmitLoading(btn, on) {
    if (!btn) return;
    btn.classList.toggle("loading", on);
    btn.disabled = on;
  }

_renderMiniCart(addData, fallbackId) {
  const miniCart = document.querySelector("mini-cart");
  if (!miniCart || typeof miniCart.renderContents !== "function") {
    console.warn("[addon-products] mini-cart nicht gefunden oder renderContents fehlt.");
    return;
  }
  if (!addData.id && fallbackId) addData.id = fallbackId;
  miniCart.renderContents(addData);
  document.dispatchEvent(new CustomEvent("cartdrawer:opened"));
  document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: addData } }));

  if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined" && PUB_SUB_EVENTS?.cartUpdate) {
    publish(PUB_SUB_EVENTS.cartUpdate, { cart: addData, source: "addon-products" });
  }
}
}

const components = {
  "addon-stepper": AddonStepper,
  "addon-item": AddonItem,
  "addon-group": AddonGroup,
  "addon-summary": AddonSummary,
  "addon-strip": AddonStrip,
  "addon-products": AddonProducts,
};

for (const [name, cls] of Object.entries(components)) {
  if (!customElements.get(name)) customElements.define(name, cls);
}